import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  untracked,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { SimTooltipComponent } from '@simOn/ui/sim-tooltip';
import { notBlank } from '@simOn/utils/form';
import { map, startWith } from 'rxjs';

const MESSAGE_MAX_LENGTH = 4000;

/**
 * Message input of the chat with its round send button; Enter sends too. While the message is being sent the
 * input is locked with the text still in it and the button shows a spinner; the text clears once the reply is in.
 */
@Component({
  selector: 'ai-agent-message-composer',
  imports: [ReactiveFormsModule, SimIconModule, SimSpinnerComponent, SimTooltipComponent],
  templateUrl: './agent-message-composer.component.html',
  styleUrl: './agent-message-composer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentMessageComposerComponent {
  /** Blocks sending (blocked assistant, history loading). */
  readonly disabled = input<boolean>(false);
  /**
   * Text of the message of this chat that is on its way: the input is locked with it until the reply arrives.
   * `null` when nothing is pending here (a send pending in another chat only disables the button).
   */
  readonly pendingMessage = input<string | null>(null);
  /** Text of a message the backend rejected; put back into the input, then `restored` is emitted. */
  readonly restoredMessage = input<string | null>(null);
  readonly send = output<string>();
  readonly restored = output<void>();

  protected readonly messageMaxLength = MESSAGE_MAX_LENGTH;
  protected readonly sendLabel = $localize`:@@AI_AGENT_SEND:Send`;
  protected readonly messageLabel = $localize`:@@AI_AGENT_MESSAGE:Message`;
  protected readonly messageControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(MESSAGE_MAX_LENGTH), notBlank]
  });

  private readonly _textarea = viewChild.required<ElementRef<HTMLTextAreaElement>>('textarea');
  private readonly _valid = toSignal(
    this.messageControl.statusChanges.pipe(
      startWith(null),
      map(() => this.messageControl.valid)
    ),
    { initialValue: false }
  );
  protected readonly sending = computed(() => this.pendingMessage() !== null);
  protected readonly canSend = computed(() => this._valid() && !this.disabled() && !this.sending());

  constructor() {
    effect(() => {
      const message = this.restoredMessage();
      if (message === null) return;
      untracked(() => {
        // Anything typed meanwhile is kept after the restored text; the locked text of the send itself is not doubled.
        const typed = this.messageControl.value.trim();
        this.messageControl.setValue(typed && typed !== message ? `${message}\n${typed}` : message);
        this.restored.emit();
      });
    });

    effect(() => {
      const pending = this.pendingMessage();
      untracked(() => {
        if (pending !== null) {
          // Also when this chat is opened again while its reply is still awaited: the text comes back with the lock.
          this.messageControl.setValue(pending);
          this.messageControl.disable();
          this._locked = true;
          return;
        }
        this.messageControl.enable();
        if (!this._locked) return;
        this._locked = false;
        // The reply is in (or the send failed and the message sits in the chat with Retry, or another chat was
        // opened): the text has done its job here. A rejected message is put back by the effect above instead.
        if (this.restoredMessage() === null) this.messageControl.reset('');
        this._textarea().nativeElement.focus();
      });
    });
  }

  /** The input holds the pending text of this chat. */
  private _locked = false;

  /** Enter sends, Shift+Enter inserts a new line; Enter during IME composition is left to the editor. */
  protected onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.isComposing) return;
    event.preventDefault();
    this.submit();
  }

  /** Emits the trimmed message; the store hands it back as `pendingMessage` until the reply is in. */
  protected submit(): void {
    if (!this.canSend()) return;
    this.send.emit(this.messageControl.value.trim());
  }
}
